/**
 * Garde-fou contre les crashs `removeChild` / `insertBefore` provoqués par une
 * mutation du DOM externe à React.
 *
 * Symptôme observé en prod (errorLogs Firestore, /onboarding) :
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * Cause : des outils tiers réécrivent les nœuds de la page sous le dos de
 * React — au premier rang Google Translate (il emballe chaque nœud texte
 * dans un `<font>`), mais aussi des extensions navigateur (Grammarly,
 * gestionnaires de mots de passe, bloqueurs de pub). React conserve sa
 * propre représentation de l'arbre ; lorsqu'il supprime un sous-arbre en
 * phase de commit, `parent.removeChild(node)` est appelé alors que le
 * `parentNode` réel du nœud a changé → exception → l'erreur remonte tout
 * l'arbre jusqu'à l'ErrorBoundary qui affiche un écran d'erreur plein.
 *
 * Particulièrement visible sur /onboarding : flow long, très riche en texte
 * français (donc fréquemment traduit) et avec de nombreuses transitions
 * d'étapes — chaque transition est un `commitDeletion` React, donc une
 * occasion de déclencher le crash.
 *
 * Correctif (contournement de référence pour facebook/react#11538) : rendre
 * `removeChild` / `insertBefore` tolérants. Quand le nœud n'est pas/plus un
 * enfant du parent ciblé, on dégrade gracieusement au lieu de throw. React
 * gère parfaitement ce cas et poursuit son rendu — la page survit.
 *
 * Le chemin nominal (`node.parentNode === this`) délègue à l'implémentation
 * native sans aucun changement de comportement ni surcoût mesurable.
 */

type DomMutationProto = {
  removeChild: (this: unknown, child: unknown) => unknown;
  insertBefore: (this: unknown, newNode: unknown, referenceNode: unknown) => unknown;
};

const GUARD_TAG = '__fitdDomNodeGuard';
const MAX_WARNINGS = 10;

/**
 * Patche `removeChild` / `insertBefore` sur `Node.prototype` (ou sur un
 * `target` fourni, pour les tests) afin de neutraliser les `NotFoundError`
 * causés par une divergence DOM externe.
 *
 * Idempotent : un prototype déjà patché est ignoré (double appel, double
 * entrypoint). Sans DOM (SSR, runner de tests sans jsdom) la fonction ne
 * fait rien.
 */
export function installDomNodeGuard(target?: object): void {
  const proto = (target ??
    (typeof Node !== 'undefined' ? Node.prototype : undefined)) as unknown as
    | DomMutationProto
    | undefined;
  if (!proto) return;

  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;
  if (
    typeof originalRemoveChild !== 'function' ||
    typeof originalInsertBefore !== 'function'
  ) {
    return;
  }
  // Déjà patché → ne pas re-wrapper.
  if ((originalRemoveChild as unknown as Record<string, unknown>)[GUARD_TAG]) return;

  let warnings = 0;
  const warn = (op: string): void => {
    if (warnings >= MAX_WARNINGS) return;
    warnings += 1;
    console.warn(
      `[domNodeGuard] ${op} ignoré : le nœud n'appartient pas au parent ` +
        'ciblé — mutation DOM externe (Google Translate ou extension ' +
        'navigateur). Crash React évité.',
    );
  };

  const guardedRemoveChild = function (this: unknown, child: unknown): unknown {
    const node = child as { parentNode?: unknown } | null | undefined;
    if (node && node.parentNode !== this) {
      // Le nœud n'est plus un enfant de ce parent : React veut le retirer,
      // c'est déjà chose faite de son point de vue → no-op.
      warn('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child);
  };
  (guardedRemoveChild as unknown as Record<string, unknown>)[GUARD_TAG] = true;

  const guardedInsertBefore = function (
    this: unknown,
    newNode: unknown,
    referenceNode: unknown,
  ): unknown {
    const ref = referenceNode as { parentNode?: unknown } | null | undefined;
    if (ref && ref.parentNode !== this) {
      // La référence a été re-parentée : on append (`referenceNode = null`)
      // pour garder `newNode` rattaché au bon parent. L'ordre peut être
      // imparfait le temps que React reconcilie, mais l'arbre reste cohérent.
      warn('insertBefore');
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
  (guardedInsertBefore as unknown as Record<string, unknown>)[GUARD_TAG] = true;

  proto.removeChild = guardedRemoveChild;
  proto.insertBefore = guardedInsertBefore;
}
